import React, { createContext, useState, useContext, useEffect, useRef, useMemo } from 'react';
import { DESIGNS, getDesignById, getAllDesigns } from '../designs/DesignRegistry';
import tokens from '../theme/tokens'; // Assuming tokens are needed for default colors

// Create design context
const DesignContext = createContext();

// Default design ID
const DEFAULT_DESIGN_ID = 'default-black'; // Updated default
const DEFAULT_STYLE = 'default'; // Default style

// --- NEW: Default structure for a custom scheme ---
const DEFAULT_CUSTOM_SCHEME = {
  id: 'custom', // Special ID
  name: 'Custom Scheme',
  category: 'custom',
  defaultStyle: 'default', // Or derive from base? Let's start with default.
  colors: {
    primary: "#555555",
    secondary: "#777777",
    tertiary: "#999999",
    quaternary: "#bbbbbb",
    accent1: "#555555",
    accent2: "#555555",
    text: "#333333",
    textSecondary: "#777777",
    sidebarText: "#333333", // NEW: Sidebar/Topbar Text Color, default to main text
    sidebarPrimary: "#555555", // NEW: Sidebar/Topbar Primary Color, default to main primary
    background: "#FFFFFF", // Keep defaults maybe?
    sidebarBackground: "#F5F5F5",
    border: "#dddddd",
  }
};

// --- NEW: Key for localStorage ---
const SAVED_SCHEMES_STORAGE_KEY = 'savedCustomColorSchemes';

// Helper to merge design colors with defaults/tokens if necessary
const mergeColorsWithDefaults = (designColors) => {
  const defaultColor = tokens.colors.neutral[500]; // Example fallback
  const requiredKeys = ['primary', 'secondary', 'tertiary', 'quaternary', 'text', 'textSecondary', 'border', 'background', 'sidebarBackground'];
  const merged = { ...designColors };
  requiredKeys.forEach(key => {
    if (!merged[key]) {
      merged[key] = defaultColor;
      console.warn(`Design color merge: Missing key '${key}', using default.`);
    }
  });
  // Add accent fallbacks if needed
  for (let i = 1; i <= 8; i++) {
    if (!merged[`accent${i}`]) {
      merged[`accent${i}`] = merged.primary || defaultColor; // Fallback accent to primary
    }
  }
  // NEW: Add sidebarText fallback
  if (!merged.sidebarText) {
    merged.sidebarText = merged.text || defaultColor; // Fallback to main text color
    console.warn(`Design color merge: Missing key 'sidebarText', using text color or default.`);
  }
  // NEW: Add sidebarPrimary fallback
  if (!merged.sidebarPrimary) {
    merged.sidebarPrimary = merged.primary || defaultColor; // Fallback to main primary color
    console.warn(`Design color merge: Missing key 'sidebarPrimary', using primary color or default.`);
  }
  return merged;
};

export function DesignProvider({ children }) {
  const designs = getAllDesigns();
  
  const isInitialized = useRef(false);
  
  const [selectedDesign, setSelectedDesignState] = useState(() => {
    const savedDesign = localStorage.getItem('selectedDesign');
    console.log('DesignContext Init: loaded selectedDesign from localStorage:', savedDesign);
    const initialDesign = (savedDesign && (DESIGNS[savedDesign] || savedDesign === 'custom')) ? savedDesign : DEFAULT_DESIGN_ID;
    console.log('DesignContext Init: initial selectedDesign state:', initialDesign);
    return initialDesign;
  });

  const [lastStandardDesignId, setLastStandardDesignId] = useState(() => {
      const savedDesign = localStorage.getItem('selectedDesign');
      let initialLastStandardId;
      if (savedDesign === 'custom') {
          const savedLastStandard = localStorage.getItem('lastStandardDesignId');
          console.log('DesignContext Init: savedDesign is custom, loaded lastStandardDesignId from localStorage:', savedLastStandard);
          initialLastStandardId = savedLastStandard || DEFAULT_DESIGN_ID;
      } else {
          initialLastStandardId = (savedDesign && DESIGNS[savedDesign]) ? savedDesign : DEFAULT_DESIGN_ID;
      }
      console.log('DesignContext Init: initial lastStandardDesignId state:', initialLastStandardId);
      return initialLastStandardId;
  });

  const [customColorScheme, setCustomColorSchemeState] = useState(() => {
    try {
      const savedScheme = localStorage.getItem('customColorScheme');
      return savedScheme ? JSON.parse(savedScheme) : DEFAULT_CUSTOM_SCHEME.colors;
    } catch (e) {
      console.error("Failed to load custom color scheme", e);
      return DEFAULT_CUSTOM_SCHEME.colors;
    }
  });

  const [activeDesign, setActiveDesign] = useState(() => {
    if (selectedDesign === 'custom') {
      return { ...DEFAULT_CUSTOM_SCHEME, colors: customColorScheme };
    }
    return getDesignById(selectedDesign);
  });

  const [savedCustomSchemes, setSavedCustomSchemes] = useState(() => {
    try {
      const savedData = localStorage.getItem(SAVED_SCHEMES_STORAGE_KEY);
      return savedData ? JSON.parse(savedData) : []; // Initialize with empty array if nothing saved
    } catch (e) {
      console.error("Failed to load saved custom schemes", e);
      return [];
    }
  });

  const wrappedSetSelectedDesign = (newDesign) => {
    console.log('DesignContext: wrappedSetSelectedDesign called with:', newDesign);
    if (designs.some(design => design.id === newDesign) || newDesign === 'custom') {
      isInitialized.current = true; // Assuming isInitialized ref exists

      setSelectedDesignState(prevSelectedDesign => {
        console.log(`DesignContext: Updating state. Prev: ${prevSelectedDesign}, New: ${newDesign}`);
        if (newDesign === 'custom' && prevSelectedDesign !== 'custom') {
            console.log(`DesignContext: Switching TO custom. Setting lastStandardDesignId to: ${prevSelectedDesign}`);
            setLastStandardDesignId(prevSelectedDesign);
        } else if (newDesign !== 'custom') {
            console.log(`DesignContext: Switching TO standard (${newDesign}). Setting lastStandardDesignId to: ${newDesign}`);
            setLastStandardDesignId(newDesign);
        }
        return newDesign;
      });
    } else {
      console.error(`Design "${newDesign}" does not exist`);
    }
  };

  useEffect(() => {
    console.log('Effect 1 Running: selectedDesign or customColorScheme changed');
    let newActiveDesign;
    if (selectedDesign === 'custom') {
      newActiveDesign = { ...DEFAULT_CUSTOM_SCHEME, colors: customColorScheme };
    } else {
      newActiveDesign = getDesignById(selectedDesign);
      if (activeDesign?.id === 'custom') {
         setCustomColorSchemeState(newActiveDesign.colors || DEFAULT_CUSTOM_SCHEME.colors);
      }
    }
    setActiveDesign(newActiveDesign);

    if (isInitialized.current) {
        console.log('Effect 1: Saving design state to localStorage');
        localStorage.setItem('selectedDesign', selectedDesign);
        localStorage.setItem('lastStandardDesignId', lastStandardDesignId);
        localStorage.setItem('customColorScheme', JSON.stringify(customColorScheme));
    }

  }, [selectedDesign, customColorScheme, lastStandardDesignId, activeDesign?.id]);

  useEffect(() => {
      console.log('Effect 5: Marking context as initialized');
      isInitialized.current = true;
  }, []);

  const resetToDesignDefaults = () => {
    console.log('Resetting to design defaults...');
    const baseDesignId = selectedDesign === 'custom' ? lastStandardDesignId : selectedDesign;
    const designToResetTo = getDesignById(baseDesignId);

    const defaultColors = designToResetTo.colors || DEFAULT_CUSTOM_SCHEME.colors;
    setCustomColorSchemeState(defaultColors);

    if (selectedDesign === 'custom') {
        setSelectedDesignState(baseDesignId);
        setLastStandardDesignId(baseDesignId);
    } else {
        setLastStandardDesignId(selectedDesign);
    }
  };

  const setCustomColor = (colorKey, value) => {
    setCustomColorSchemeState(prevScheme => {
        const newScheme = { ...prevScheme, [colorKey]: value };
        return newScheme;
    });
  };

  const setBaseForCustomScheme = (baseDesignId) => {
    const baseDesign = getDesignById(baseDesignId);
    if (baseDesign) {
      const newCustomColors = { ...baseDesign.colors };
      setCustomColorSchemeState(newCustomColors);
      console.log(`DesignContext: Setting base for custom scheme. Setting lastStandardDesignId to: ${baseDesignId}`);
      setLastStandardDesignId(baseDesignId);
    }
  };

  const saveCustomScheme = (name) => {
    if (!name || !name.trim()) {
      console.error("Cannot save scheme without a name.");
      return;
    }
    const newScheme = {
      id: Date.now(),
      name: name.trim(),
      colors: { ...customColorScheme },
    };

    setSavedCustomSchemes(prevSchemes => {
      const updatedSchemes = [...prevSchemes, newScheme];
      localStorage.setItem(SAVED_SCHEMES_STORAGE_KEY, JSON.stringify(updatedSchemes));
      console.log('Saved custom scheme:', newScheme.name);
      return updatedSchemes;
    });
  };

  const loadCustomScheme = (schemeId) => {
    const schemeToLoad = savedCustomSchemes.find(s => s.id === schemeId);
    if (schemeToLoad) {
      console.log('Loading custom scheme:', schemeToLoad.name);
      setFullCustomColorScheme({ ...schemeToLoad.colors });
    } else {
      console.error("Scheme not found for ID:", schemeId);
    }
  };

  const deleteCustomScheme = (schemeId) => {
    setSavedCustomSchemes(prevSchemes => {
      const updatedSchemes = prevSchemes.filter(s => s.id !== schemeId);
      localStorage.setItem(SAVED_SCHEMES_STORAGE_KEY, JSON.stringify(updatedSchemes));
      console.log('Deleted custom scheme with ID:', schemeId);
      return updatedSchemes;
    });
  };

  const setFullCustomColorScheme = (newSchemeObject) => {
    if (newSchemeObject && typeof newSchemeObject === 'object') {
      const mergedScheme = mergeColorsWithDefaults(newSchemeObject);
      console.log("DesignContext: Setting full custom color scheme (merged):", mergedScheme);
      setCustomColorSchemeState(mergedScheme);
    } else {
      console.error("DesignContext: Invalid object passed to setFullCustomColorScheme:", newSchemeObject);
    }
  };

  const value = useMemo(() => {
    console.log(`useMemo running. selectedDesign: ${selectedDesign}, lastStandardDesignId: ${lastStandardDesignId}`);

    const isCustom = selectedDesign === 'custom';
    const baseIdForColors = isCustom ? lastStandardDesignId : selectedDesign;
    const baseDesignData = getDesignById(baseIdForColors);

    const activeDesignObject = isCustom
        ? { ...DEFAULT_CUSTOM_SCHEME, colors: customColorScheme }
        : baseDesignData;

    // ENHANCED: Ensure currentColors always has properly resolved colors
    let currentColors = {};
    
    if (isCustom) {
      // For custom schemes, use the custom color scheme with fallbacks
      currentColors = mergeColorsWithDefaults(customColorScheme || {});
    } else if (baseDesignData?.colors) {
      // For preset schemes, use the design's colors with fallbacks
      currentColors = mergeColorsWithDefaults(baseDesignData.colors);
    } else {
      // Fallback to default colors if something goes wrong
      console.warn(`DesignContext: No colors found for design "${selectedDesign}", using defaults`);
      currentColors = mergeColorsWithDefaults({});
    }
    
    // DEBUG: Log the current colors to ensure they're properly resolved
    console.log('DesignContext: Current colors resolved:', {
      selectedDesign,
      isCustom,
      baseIdForColors,
      hasBaseDesignData: !!baseDesignData,
      hasColors: !!activeDesignObject?.colors,
      currentColors: currentColors,
      colorKeys: Object.keys(currentColors),
      sampleColors: {
        primary: currentColors.primary,
        text: currentColors.text,
        background: currentColors.background
      }
    });

    return {
      selectedDesign,
      setSelectedDesign: wrappedSetSelectedDesign,
      activeDesign: activeDesignObject,
      designs: designs,
      resetToDesignDefaults,
      customColorScheme,
      setCustomColor,
      setBaseForCustomScheme,
      setFullCustomColorScheme,
      lastStandardDesignId,
      savedCustomSchemes,
      saveCustomScheme,
      loadCustomScheme,
      deleteCustomScheme,
      currentColors,
    };
  }, [
      selectedDesign,
      customColorScheme,
      lastStandardDesignId,
      savedCustomSchemes,
      designs,
      wrappedSetSelectedDesign,
      resetToDesignDefaults,
      setCustomColor,
      setBaseForCustomScheme,
      setFullCustomColorScheme,
      saveCustomScheme,
      loadCustomScheme,
      deleteCustomScheme,
  ]);

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
}

export function useDesign() {
  const context = useContext(DesignContext);
  if (!context) {
    throw new Error('useDesign must be used within a DesignProvider');
  }
  return context;
}

// Remove default export if not needed elsewhere like this
// export default { DesignContext, DesignProvider, useDesign }; 